import {useEffect, useRef} from "react";

const MODAL_CLOSE_TIMEOUT = 300;

export default function useModal(open: boolean, setOpen: (open: boolean) => void, onAfterClose?: () => void) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if(!dialogRef.current) return;

    const handleClose = () => {
      setOpen(false);
    }

    dialogRef.current.addEventListener('close', handleClose);

    return () => {
      dialogRef.current?.removeEventListener('close', handleClose);
    }
  }, []);

  useEffect(() => {
    if(!dialogRef.current) return;

    if(open) {
      dialogRef.current.showModal();

      // Auto-focus the first focusable input element
      setTimeout(() => {
        const focusable = dialogRef.current?.querySelector<HTMLElement>(
          'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled])'
        );
        focusable?.focus();
      }, 0);
    } else {
      dialogRef.current.dataset.closing = 'true';

      setTimeout(() => {
        dialogRef.current?.close();
        delete dialogRef.current?.dataset.closing;
        onAfterClose?.();
      }, MODAL_CLOSE_TIMEOUT);
    }
  }, [open, onAfterClose]);

  // Close modal when clicking outside (on the backdrop)
  useEffect(() => {
    if(!dialogRef.current) return;

    const handleBackdropClick = (e: MouseEvent) => {
      if (e.target === dialogRef.current) {
        setOpen(false);
      }
    };

    dialogRef.current.addEventListener('click', handleBackdropClick);

    return () => {
      dialogRef.current?.removeEventListener('click', handleBackdropClick);
    };
  }, [setOpen]);
  
  return { dialogRef };
}